import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  EthereumProvider,
} from "@walletconnect/ethereum-provider";

import Swal from "sweetalert2";

import Context from "./context/AppContext";

import WalletConnectModal from "./components/wallet/WalletConnectModal";
import ScrollToHashElement from "./components/common/ScrollToHashElement";

import TravelersPage from "./pages/TravelersPage";

import useSessionWatcher from "./hooks/useSessionWatcher";

import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import DocumentsPage from "./pages/DocumentsPage";
import UpcomingToursPage from "./pages/UpcomingToursPage";
import CompletedToursPage from "./pages/CompletedToursPage";
import MyToursPage from "./pages/MyToursPage";
import ProfilePage from "./pages/ProfilePage";
import CoPassengerProfilePage from "./pages/CoPassengerProfilePage";

import {
  clearAuth,
  logoutApi,
  walletLogin,
} from "./services/api";

import "bootstrap/dist/css/bootstrap.min.css";

import "./styles/global.css";
import "./styles/light-theme.css";
import "./styles/modal.css";
import "./styles/responsive.css";

/* =====================================================
   NETWORK
===================================================== */

const network = {
  SCAN_LINK:
    "https://rpc.trustbitcoin.io/",

  NAME:
    "Trust Bitcoin Mainnet",

  SYMBOL:
    "TBC",

  ID:
    10791,

  CHAINID_HEX:
    "0x2a27",

  EXPLORER:
    "https://scan.trustbitcoin.io/",
};

/*
 * Remember which wallet provider user used.
 *
 * metamask
 * walletconnect
 */
const WALLET_TYPE_KEY =
  "travel_wallet_type";

/* =====================================================
   HELPERS
===================================================== */

const normalizeAddress = (address) => {
  return String(
    address || ""
  )
    .trim()
    .toLowerCase();
};

const normalizeChainId = (chainId) => {
  if (
    chainId === null ||
    chainId === undefined
  ) {
    return 0;
  }

  if (
    typeof chainId === "number"
  ) {
    return chainId;
  }

  if (
    typeof chainId === "bigint"
  ) {
    return Number(chainId);
  }

  const value =
    String(chainId).trim();

  if (!value) {
    return 0;
  }

  if (
    value.toLowerCase()
      .startsWith("0x")
  ) {
    return parseInt(
      value,
      16
    );
  }

  return Number(value);
};

/* =====================================================
   SESSION WATCHER
===================================================== */

function SessionWatcher() {
  useSessionWatcher();

  return null;
}

/* =====================================================
   PRIVATE ROUTES
===================================================== */

function RequireAuth() {
  const location =
    useLocation();

  const {
    user,
  } = useContext(Context);

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: location,
        }}
      />
    );
  }

  return <Outlet />;
}

/* =====================================================
   APP
===================================================== */

function App() {
  const navigate =
    useNavigate();

  /* -------------------------------------------------
     PROVIDER REFS
  ------------------------------------------------- */

  const providerRef =
    useRef(null);

  /*
   * Wallet used for actual signed login.
   */
  const authenticatedWalletRef =
    useRef(null);

  /*
   * metamask / walletconnect
   */
  const walletTypeRef =
    useRef(
      localStorage.getItem(
        WALLET_TYPE_KEY
      ) || ""
    );

  /*
   * Prevent duplicate logout when:
   *
   * disconnect()
   * ->
   * disconnect event
   * ->
   * logout again
   */
  const logoutInProgressRef =
    useRef(false);

  /*
   * Prevent multiple restore calls
   * in React StrictMode.
   */
  const restoreStartedRef =
    useRef(false);

  /*
   * Exact event handlers cleanup.
   */
  const providerCleanupRef =
    useRef(null);

  /* -------------------------------------------------
     STATES
  ------------------------------------------------- */

  const [
    modalIsOpen,
    setIsOpen,
  ] = useState(false);

  const [
    provider,
    setProvider,
  ] = useState(null);

  const [
    account,
    setAccount,
  ] = useState(null);

  const [
    walletRestoring,
    setWalletRestoring,
  ] = useState(() => {
    const savedUser =
      sessionStorage.getItem(
        "user"
      );

    return Boolean(
      savedUser &&
        savedUser !==
          "undefined"
    );
  });

  const [
    user,
    setUser,
  ] = useState(() => {
    const savedUser =
      sessionStorage.getItem(
        "user"
      );

    if (
      !savedUser ||
      savedUser ===
        "undefined"
    ) {
      return null;
    }

    try {
      return JSON.parse(
        savedUser
      );
    } catch (_) {
      sessionStorage.removeItem(
        "user"
      );

      sessionStorage.removeItem(
        "isLogin"
      );

      localStorage.removeItem(
        "token"
      );

      return null;
    }
  });

  /* -------------------------------------------------
     WALLETCONNECT PROJECT
  ------------------------------------------------- */

  const projectId =
    process.env
      .REACT_APP_WALLETCONNECT_PROJECT_ID ||
    "376766ad58cb967480dae7850898ae28";

  /* =================================================
     KEEP SIGNED WALLET REF UPDATED
  ================================================= */

  useEffect(() => {
    authenticatedWalletRef.current =
      user?.wallet_address
        ? normalizeAddress(
            user.wallet_address
          )
        : null;
  }, [user]);

  /* =================================================
     AUTH EVENT SYNC
  ================================================= */

  useEffect(() => {
    const syncAuth = () => {
      try {
        const savedUser =
          JSON.parse(
            sessionStorage.getItem(
              "user"
            ) || "null"
          );

        setUser(
          savedUser
        );
      } catch (_) {
        setUser(null);
      }
    };

    window.addEventListener(
      "auth:changed",
      syncAuth
    );

    return () => {
      window.removeEventListener(
        "auth:changed",
        syncAuth
      );
    };
  }, []);

  /* =================================================
     REMOVE OLD PROVIDER LISTENERS
  ================================================= */

  const detachProviderEvents =
    () => {
      if (
        typeof providerCleanupRef
          .current === "function"
      ) {
        providerCleanupRef
          .current();
      }

      providerCleanupRef.current =
        null;
    };

  /* =================================================
     READ PROVIDER ACCOUNTS
  ================================================= */

  const getProviderAccounts =
    async (walletProvider) => {
      if (
        !walletProvider ||
        typeof walletProvider
          .request !== "function"
      ) {
        return [];
      }

      /*
       * WalletConnect already exposes
       * restored accounts directly.
       */
      if (
        Array.isArray(
          walletProvider.accounts
        ) &&
        walletProvider.accounts
          .length > 0
      ) {
        return walletProvider
          .accounts;
      }

      const accounts =
        await walletProvider.request({
          method:
            "eth_accounts",
        });

      return Array.isArray(
        accounts
      )
        ? accounts
        : [];
    };

  /* =================================================
     READ PROVIDER CHAIN
  ================================================= */

  const getProviderChainId =
    async (walletProvider) => {
      try {
        const chainId =
          await walletProvider.request(
            {
              method:
                "eth_chainId",
            }
          );

        return normalizeChainId(
          chainId
        );
      } catch (_) {
        return normalizeChainId(
          walletProvider
            ?.chainId
        );
      }
    };

  /* =================================================
     CENTRAL LOGOUT
  ================================================= */

  const performLogout =
    async ({
      title = "",
      message = "",
      disconnectWallet = true,
    } = {}) => {
      if (
        logoutInProgressRef.current
      ) {
        return;
      }

      logoutInProgressRef.current =
        true;

      const currentProvider =
        providerRef.current;

      const currentWalletType =
        walletTypeRef.current ||
        localStorage.getItem(
          WALLET_TYPE_KEY
        );

      /*
       * Immediately remove React access.
       */
      setUser(null);

      setAccount(null);

      setProvider(null);

      setWalletRestoring(false);

      /*
       * Send user away from protected pages.
       */
      navigate(
        "/login",
        {
          replace: true,
        }
      );

      try {
        /* ---------------------------------
           BACKEND LOGOUT
        --------------------------------- */

        try {
          await logoutApi();
        } catch (error) {
          console.warn(
            "Backend logout failed:",
            error
          );
        }

        /* ---------------------------------
           DISCONNECT REAL WALLET SESSION
        --------------------------------- */

        if (
          disconnectWallet
        ) {
          /*
           * WalletConnect
           */
          if (
            currentWalletType ===
              "walletconnect" &&
            currentProvider &&
            typeof currentProvider
              .disconnect ===
              "function"
          ) {
            try {
              await currentProvider
                .disconnect();
            } catch (error) {
              console.warn(
                "WalletConnect disconnect failed:",
                error
              );
            }
          }

          /*
           * MetaMask / injected wallet
           */
          if (
            currentWalletType ===
              "metamask" &&
            window.ethereum
          ) {
            try {
              await window.ethereum.request(
                {
                  method:
                    "wallet_revokePermissions",

                  params: [
                    {
                      eth_accounts:
                        {},
                    },
                  ],
                }
              );
            } catch (error) {
              /*
               * Some injected wallets
               * may not support revoke.
               *
               * App logout will still work.
               */
              console.warn(
                "Wallet permission revoke failed/unsupported:",
                error
              );
            }
          }
        }
      } finally {
        /* ---------------------------------
           CLEAN PROVIDER EVENTS
        --------------------------------- */

        detachProviderEvents();

        /* ---------------------------------
           CLEAN APP AUTH
        --------------------------------- */

        clearAuth();

        providerRef.current =
          null;

        authenticatedWalletRef.current =
          null;

        walletTypeRef.current =
          "";

        localStorage.removeItem(
          WALLET_TYPE_KEY
        );

        setProvider(null);

        setAccount(null);

        setUser(null);

        logoutInProgressRef.current =
          false;

        /* ---------------------------------
           OPTIONAL MESSAGE
        --------------------------------- */

        if (
          title &&
          message
        ) {
          await Swal.fire({
            icon: "warning",

            title,

            text: message,

            confirmButtonText:
              "Sign In Again",

            allowOutsideClick:
              false,
          });
        }
      }
    };

  /* =================================================
     PROVIDER EVENTS
  ================================================= */

  const bindProviderEvents =
    (walletProvider) => {
      /*
       * Remove old listeners before
       * binding new provider.
       */
      detachProviderEvents();

      /* ---------------------------------
         ACCOUNT CHANGE
      --------------------------------- */

      const accountsChangedHandler =
        async (accounts) => {
          if (
            logoutInProgressRef.current
          ) {
            return;
          }

          const nextAccount =
            accounts?.[0] ||
            null;

          console.log(
            "accountsChanged:",
            nextAccount
          );

          /*
           * Wallet account access removed.
           */
          if (!nextAccount) {
            if (
              authenticatedWalletRef
                .current
            ) {
              await performLogout({
                title:
                  "Wallet Disconnected",

                message:
                  "Your wallet was disconnected. Please connect and sign in again.",

                disconnectWallet:
                  false,
              });
            } else {
              setAccount(null);
            }

            return;
          }

          const nextNormalized =
            normalizeAddress(
              nextAccount
            );

          const signedWallet =
            authenticatedWalletRef
              .current;

          /*
           * User hasn't authenticated yet.
           */
          if (!signedWallet) {
            setAccount(
              nextAccount
            );

            return;
          }

          /*
           * Same wallet:
           * no logout.
           */
          if (
            nextNormalized ===
            signedWallet
          ) {
            setAccount(
              nextAccount
            );

            return;
          }

          /*
           * Different account:
           * force logout.
           */
          await performLogout({
            title:
              "Wallet Account Changed",

            message:
              "Your wallet account changed. For security, please sign in again with the selected wallet.",

            disconnectWallet:
              true,
          });
        };

      /* ---------------------------------
         CHAIN CHANGE
      --------------------------------- */

      const chainChangedHandler =
        async (chainId) => {
          if (
            logoutInProgressRef.current
          ) {
            return;
          }

          const nextChainId =
            normalizeChainId(
              chainId
            );

          console.log(
            "chainChanged:",
            nextChainId
          );

          /*
           * Ignore duplicate same-chain event.
           */
          if (
            nextChainId ===
            network.ID
          ) {
            return;
          }

          /*
           * Only authenticated users need
           * forced logout.
           */
          if (
            authenticatedWalletRef
              .current
          ) {
            await performLogout({
              title:
                "Network Changed",

              message:
                "Your wallet network changed. Please switch back to Trust Bitcoin Mainnet and sign in again.",

              disconnectWallet:
                true,
            });
          }
        };

      /* ---------------------------------
         WALLET DISCONNECT
      --------------------------------- */

      const disconnectHandler =
        async () => {
          if (
            logoutInProgressRef.current
          ) {
            return;
          }

          console.log(
            "Wallet provider disconnected"
          );

          if (
            authenticatedWalletRef
              .current
          ) {
            await performLogout({
              title:
                "Wallet Disconnected",

              message:
                "Your wallet session was disconnected. Please connect and sign in again.",

              disconnectWallet:
                false,
            });
          } else {
            setAccount(null);
            setProvider(null);
          }
        };

      /* ---------------------------------
         ATTACH EVENTS
      --------------------------------- */

      if (
        typeof walletProvider?.on ===
        "function"
      ) {
        walletProvider.on(
          "accountsChanged",
          accountsChangedHandler
        );

        walletProvider.on(
          "chainChanged",
          chainChangedHandler
        );

        walletProvider.on(
          "disconnect",
          disconnectHandler
        );
      }

      /* ---------------------------------
         EXACT CLEANUP
      --------------------------------- */

      providerCleanupRef.current =
        () => {
          if (
            typeof walletProvider
              ?.removeListener ===
            "function"
          ) {
            walletProvider.removeListener(
              "accountsChanged",
              accountsChangedHandler
            );

            walletProvider.removeListener(
              "chainChanged",
              chainChangedHandler
            );

            walletProvider.removeListener(
              "disconnect",
              disconnectHandler
            );
          } else if (
            typeof walletProvider
              ?.off ===
            "function"
          ) {
            walletProvider.off(
              "accountsChanged",
              accountsChangedHandler
            );

            walletProvider.off(
              "chainChanged",
              chainChangedHandler
            );

            walletProvider.off(
              "disconnect",
              disconnectHandler
            );
          }
        };
    };

  /* =================================================
     VERIFY + REGISTER PROVIDER
  ================================================= */

  const registerProvider =
    async ({
      walletProvider,
      walletType,
      expectedWallet = null,
    }) => {
      if (
        !walletProvider
      ) {
        throw new Error(
          "Wallet provider is unavailable."
        );
      }

      /* ---------------------------------
         ACCOUNT
      --------------------------------- */

      const accounts =
        await getProviderAccounts(
          walletProvider
        );

      const connectedAccount =
        accounts?.[0];

      if (
        !connectedAccount
      ) {
        throw new Error(
          "No wallet account was found."
        );
      }

      /* ---------------------------------
         NETWORK
      --------------------------------- */

      const chainId =
        await getProviderChainId(
          walletProvider
        );

      if (
        chainId !== network.ID
      ) {
        throw new Error(
          "Please switch your wallet to Trust Bitcoin Mainnet."
        );
      }

      /* ---------------------------------
         AUTHENTICATED WALLET MATCH
      --------------------------------- */

      if (
        expectedWallet &&
        normalizeAddress(
          connectedAccount
        ) !==
          normalizeAddress(
            expectedWallet
          )
      ) {
        throw new Error(
          "Connected wallet does not match the wallet used to sign in."
        );
      }

      /* ---------------------------------
         SAVE CENTRAL PROVIDER STATE
      --------------------------------- */

      providerRef.current =
        walletProvider;

      walletTypeRef.current =
        walletType;

      localStorage.setItem(
        WALLET_TYPE_KEY,
        walletType
      );

      setProvider(
        walletProvider
      );

      setAccount(
        connectedAccount
      );

      if (
        expectedWallet
      ) {
        authenticatedWalletRef.current =
          normalizeAddress(
            expectedWallet
          );
      }

      /* ---------------------------------
         EVENTS
      --------------------------------- */

      bindProviderEvents(
        walletProvider
      );

      return connectedAccount;
    };

  /* =================================================
     LOGIN WITH CONNECTED PROVIDER
  ================================================= */

  const authenticateWallet =
    async ({
      walletProvider,
      walletType,
    }) => {
      /*
       * First make sure provider is
       * on correct network + has account.
       */
      const connectedAccount =
        await registerProvider({
          walletProvider,
          walletType,
        });

      /*
       * Existing authenticated user:
       *
       * do NOT sign again.
       */
      if (
        user?.wallet_address
      ) {
        const loginWallet =
          normalizeAddress(
            user.wallet_address
          );

        if (
          loginWallet !==
          normalizeAddress(
            connectedAccount
          )
        ) {
          throw new Error(
            "Connected wallet does not match your signed-in wallet."
          );
        }

        authenticatedWalletRef.current =
          loginWallet;

        return;
      }

      /* ---------------------------------
         NEW LOGIN SIGNATURE
      --------------------------------- */

      const result =
        await walletLogin(
          walletProvider
        );

      if (
        !result?.user
      ) {
        throw new Error(
          "Wallet authentication failed."
        );
      }

      /*
       * Re-read account after signature
       * in case account changed while
       * signing.
       */
      const latestAccounts =
        await getProviderAccounts(
          walletProvider
        );

      const latestAccount =
        latestAccounts?.[0];

      if (
        !latestAccount
      ) {
        throw new Error(
          "Wallet disconnected during sign in."
        );
      }

      const authenticatedAddress =
        normalizeAddress(
          result.user
            ?.wallet_address ||
            latestAccount
        );

      if (
        authenticatedAddress !==
        normalizeAddress(
          latestAccount
        )
      ) {
        throw new Error(
          "Signed wallet does not match the currently selected wallet."
        );
      }

      authenticatedWalletRef.current =
        authenticatedAddress;

      setAccount(
        latestAccount
      );

      setUser(
        result.user
      );

      localStorage.setItem(
        WALLET_TYPE_KEY,
        walletType
      );

      walletTypeRef.current =
        walletType;

      navigate(
        "/dashboard",
        {
          replace: true,
        }
      );
    };

  /* =================================================
     METAMASK CONNECT
  ================================================= */

  const handleMetaMaskConnect =
    async () => {
      setIsOpen(false);

      try {
        if (
          !window.ethereum
        ) {
          throw new Error(
            "Please install MetaMask or open this page in a wallet browser."
          );
        }

        /*
         * ONLY first/manual connection
         * requests account permission.
         */
        const accounts =
          await window.ethereum.request(
            {
              method:
                "eth_requestAccounts",
            }
          );

        if (
          !accounts?.[0]
        ) {
          throw new Error(
            "No wallet account selected."
          );
        }

        await authenticateWallet({
          walletProvider:
            window.ethereum,

          walletType:
            "metamask",
        });
      } catch (error) {
        console.error(
          "MetaMask connection error:",
          error
        );

        Swal.fire(
          "Wallet login failed",
          error?.message ||
            "Could not connect wallet.",
          "error"
        );
      }
    };

  /* =================================================
     CREATE WALLETCONNECT PROVIDER
  ================================================= */

  const createWalletConnectProvider =
    async ({
      showQrModal,
    }) => {
      return await EthereumProvider.init({
        projectId,

        showQrModal,

        /*
         * Only required for actual
         * connect flow.
         */
        ...(showQrModal
          ? {
              qrModalOptions:
                {
                  themeMode:
                    "light",

                  explorerRecommendedWalletIds:
                    [
                      "a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393",

                      "fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa",
                    ],

                  explorerExcludedWalletIds:
                    "ALL",
                },
            }
          : {}),

        rpcMap: {
          [network.ID]:
            "https://rpc.trustbitcoin.io",
        },

        chains: [
          network.ID,
        ],
      });
    };

  /* =================================================
     WALLETCONNECT CONNECT
  ================================================= */

  const handleWalletConnect =
    async () => {
      setIsOpen(false);

      try {
        const wcProvider =
          await createWalletConnectProvider({
            showQrModal: true,
          });

        /*
         * First connection only.
         *
         * Existing session uses it
         * automatically if present.
         */
        await wcProvider.enable();

        await authenticateWallet({
          walletProvider:
            wcProvider,

          walletType:
            "walletconnect",
        });
      } catch (error) {
        console.error(
          "WalletConnect error:",
          error
        );

        Swal.fire(
          "Wallet login failed",
          error?.message ||
            "Could not connect wallet.",
          "error"
        );
      }
    };

  /* =================================================
     RESTORE METAMASK AFTER REFRESH
  ================================================= */

  const restoreMetaMask =
    async ({
      signedWallet,
      strict = true,
    }) => {
      if (
        !window.ethereum
      ) {
        if (strict) {
          throw new Error(
            "MetaMask provider is unavailable."
          );
        }

        return false;
      }

      /*
       * IMPORTANT:
       *
       * eth_accounts does NOT ask
       * to connect again.
       */
      const accounts =
        await window.ethereum.request(
          {
            method:
              "eth_accounts",
          }
        );

      const restoredAccount =
        accounts?.[0];

      if (
        !restoredAccount
      ) {
        if (strict) {
          throw new Error(
            "MetaMask connection is no longer available."
          );
        }

        return false;
      }

      /*
       * Old session fallback:
       * only claim MetaMask if
       * account actually matches.
       */
      if (
        normalizeAddress(
          restoredAccount
        ) !==
        normalizeAddress(
          signedWallet
        )
      ) {
        if (strict) {
          throw new Error(
            "MetaMask account changed."
          );
        }

        return false;
      }

      const chainId =
        await getProviderChainId(
          window.ethereum
        );

      if (
        chainId !== network.ID
      ) {
        if (strict) {
          throw new Error(
            "MetaMask network changed."
          );
        }

        return false;
      }

      await registerProvider({
        walletProvider:
          window.ethereum,

        walletType:
          "metamask",

        expectedWallet:
          signedWallet,
      });

      return true;
    };

  /* =================================================
     RESTORE WALLETCONNECT AFTER REFRESH
  ================================================= */

  const restoreWalletConnect =
    async ({
      signedWallet,
      strict = true,
    }) => {
      const wcProvider =
        await createWalletConnectProvider({
          /*
           * NEVER open QR on refresh.
           */
          showQrModal: false,
        });

      /*
       * init() loads persisted WC session.
       *
       * Do NOT call enable() here.
       */
      if (
        !wcProvider?.session
      ) {
        if (strict) {
          throw new Error(
            "WalletConnect session expired."
          );
        }

        return false;
      }

      const accounts =
        await getProviderAccounts(
          wcProvider
        );

      const restoredAccount =
        accounts?.[0];

      if (
        !restoredAccount
      ) {
        if (strict) {
          throw new Error(
            "WalletConnect account could not be restored."
          );
        }

        return false;
      }

      if (
        normalizeAddress(
          restoredAccount
        ) !==
        normalizeAddress(
          signedWallet
        )
      ) {
        if (strict) {
          throw new Error(
            "WalletConnect account changed."
          );
        }

        return false;
      }

      const chainId =
        await getProviderChainId(
          wcProvider
        );

      if (
        chainId !== network.ID
      ) {
        if (strict) {
          throw new Error(
            "WalletConnect network changed."
          );
        }

        return false;
      }

      await registerProvider({
        walletProvider:
          wcProvider,

        walletType:
          "walletconnect",

        expectedWallet:
          signedWallet,
      });

      return true;
    };

  /* =================================================
     RESTORE WALLET ON PAGE REFRESH
  ================================================= */

  useEffect(() => {
    if (
      restoreStartedRef.current
    ) {
      return;
    }

    restoreStartedRef.current =
      true;

    const restoreWallet =
      async () => {
        /*
         * No app login:
         * nothing to restore.
         */
        if (
          !user?.wallet_address
        ) {
          setWalletRestoring(
            false
          );

          return;
        }

        setWalletRestoring(
          true
        );

        const signedWallet =
          normalizeAddress(
            user.wallet_address
          );

        authenticatedWalletRef.current =
          signedWallet;

        const savedWalletType =
          localStorage.getItem(
            WALLET_TYPE_KEY
          );

        try {
          /* =================================
             KNOWN METAMASK SESSION
          ================================= */

          if (
            savedWalletType ===
            "metamask"
          ) {
            await restoreMetaMask({
              signedWallet,
              strict: true,
            });

            console.log(
              "MetaMask restored successfully"
            );

            return;
          }

          /* =================================
             KNOWN WALLETCONNECT SESSION
          ================================= */

          if (
            savedWalletType ===
            "walletconnect"
          ) {
            await restoreWalletConnect({
              signedWallet,
              strict: true,
            });

            console.log(
              "WalletConnect restored successfully"
            );

            return;
          }

          /* =================================
             OLD SESSION:
             wallet type not saved yet
          ================================= */

          let restored =
            false;

          /*
           * Try injected wallet first.
           */
          if (
            window.ethereum
          ) {
            try {
              restored =
                await restoreMetaMask({
                  signedWallet,
                  strict: false,
                });
            } catch (_) {
              restored =
                false;
            }
          }

          /*
           * If signed wallet wasn't
           * MetaMask, try existing WC
           * persisted session.
           */
          if (!restored) {
            try {
              restored =
                await restoreWalletConnect({
                  signedWallet,
                  strict: false,
                });
            } catch (_) {
              restored =
                false;
            }
          }

          if (!restored) {
            throw new Error(
              "Previous wallet session could not be restored."
            );
          }
        } catch (error) {
          console.error(
            "Wallet restore failed:",
            error
          );

          /*
           * This is NOT a normal refresh
           * logout.
           *
           * It means:
           * - wallet permission removed
           * - account changed
           * - chain changed
           * - WC session expired/deleted
           */
          await performLogout({
            title:
              "Wallet Session Changed",

            message:
              "Your saved wallet session no longer matches your signed-in account. Please connect and sign in again.",

            disconnectWallet:
              false,
          });
        } finally {
          setWalletRestoring(
            false
          );
        }
      };

    restoreWallet();

    /*
     * No cleanup here intentionally.
     *
     * App owns wallet provider for the
     * full application lifecycle.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =================================================
     MANUAL LOGOUT BUTTON
  ================================================= */

  const handleManualLogout =
    async () => {
      await performLogout({
        disconnectWallet:
          true,
      });
    };

  /* =================================================
     CONTEXT
  ================================================= */

  const ctxValue =
    useMemo(
      () => ({
        account,

        provider,

        user,

        setUser,

        network,

        clearAuth,

        walletRestoring,

        /*
         * Open connect modal
         */
        onConnect:
          () =>
            setIsOpen(true),

        /*
         * CENTRAL LOGOUT
         */
        onDisconnect:
          handleManualLogout,
      }),
      [
        account,
        provider,
        user,
        walletRestoring,
      ]
    );

  /* =================================================
     UI
  ================================================= */

  return (
    <>
      <Context.Provider
        value={ctxValue}
      >
        <SessionWatcher />

        <ScrollToHashElement />

        <Routes>
          <Route
            path="/"
            element={
              <HomePage />
            }
          />

          <Route
            path="/login"
            element={
              <LoginPage />
            }
          />

          <Route
            element={
              <RequireAuth />
            }
          >
            <Route
              path="/dashboard"
              element={
                <DashboardPage />
              }
            />

            <Route
              path="/documents"
              element={
                <DocumentsPage />
              }
            />

            <Route
              path="/documents/:slug"
              element={
                <DocumentsPage />
              }
            />

            <Route
              path="/my-travelers"
              element={
                <TravelersPage />
              }
            />

            <Route
              path="/upcoming-tours"
              element={
                <UpcomingToursPage />
              }
            />

            <Route
              path="/completed-tours"
              element={
                <CompletedToursPage />
              }
            />

            <Route
              path="/my-tours"
              element={
                <MyToursPage />
              }
            />

            <Route
              path="/profile"
              element={
                <ProfilePage />
              }
            />

            <Route
              path="/co-passenger-profile"
              element={
                <CoPassengerProfilePage />
              }
            />
          </Route>
        </Routes>
      </Context.Provider>

      <WalletConnectModal
        onWalletConnect={
          handleWalletConnect
        }
        onMetamaskConnect={
          handleMetaMaskConnect
        }
        modalIsOpen={
          modalIsOpen
        }
        setIsOpen={
          setIsOpen
        }
      />
    </>
  );
}

export default App;